import { useCallback, useEffect, useState } from "react";
import { User, Wrench } from "lucide-react";

import withDragAndDrop, {
  type EventInteractionArgs,
} from "react-big-calendar/lib/addons/dragAndDrop";
import { Calendar, dayjsLocalizer } from "react-big-calendar";

import dayjs from "dayjs";

import "@/styles/calendar.css";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import KanbanDrawerContent from "@/routes/(planner-screens)/components/kanban/kanban-drawer-content";

import { useQueryParams } from "@/hooks/use-query-params";
import { useGetData, usePutData } from "@/hooks/services/use-query-hooks";
import {
  JobOrderList,
  UpdateJobOrder,
} from "@/hooks/services/pps/handlers/job-orders-api";

import {
  Job,
  JobPayload,
  JobResponse,
  Machine,
  Technician,
  TechnicianType,
} from "@/hooks/services/pps/types/job-orders.type";

import { defineView, tranformEvent } from "../../utils/utils";

import { capitalize, showPriorityIcon } from "@/lib/utils";

import { useSortStore } from "@/stores/sort-store";
import { mapper } from "../../utils/utils";
import { useFilterStore } from "@/stores/filter-store";
import { defaultSelectedItem, ViewPps } from "../../utils/constants";

const localizer = dayjsLocalizer(dayjs);
const DragAndDropCalendar = withDragAndDrop(Calendar);

type CalendarView = "month" | "week" | "day" | "agenda";
export type stringOrDate = string | Date;

interface CalendarItem {
  id: string | number | null;
  title: string;
  desc: string;
  start: stringOrDate;
  end: stringOrDate;
}

interface ExtendedCalendarItem extends CalendarItem {
  technicians: TechnicianType[];
  // machines: Machine[];
  // issues?: EventIssues[];
  priority: number | null;
  standardHours: string;
}

interface DrawerItem {
  show: boolean;
  task: Job;
}

// export const Route = createFileRoute("/(planner-screens)/planner/calendar")({
//   component: RouteComponent,
// });

export function CalendarComponent() {
  const { params } = useQueryParams();
  const { sorts } = useSortStore();
  const { filterPayload } = useFilterStore();

  const { data } = useGetData<JobResponse>(
    JobOrderList({
      ...params,
      page: 1,
      pageSize: 100,
      viewId: ViewPps.calendar.id,
      ...(sorts.length > 0 && { encodeQuery: { sorts: mapper(sorts) } }),
      ...(filterPayload.length > 0 && {
        encodeQuery: { filters: filterPayload },
      }),
    }),
  );

  const { mutate } = usePutData<JobPayload, any>(UpdateJobOrder());

  const [events, setEvents] = useState<ExtendedCalendarItem[]>([]);

  const [calendarView, setCalendarView] = useState<CalendarView>("month");
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedItem, setSelectedItem] = useState<DrawerItem>({
    show: false,
    task: defaultSelectedItem,
  });

  useEffect(() => {
    if (data?.data) {
      // need to transform cause the react big calendar expect events to have a specific structure
      const transformBody: ExtendedCalendarItem[] = data?.data.map(
        (item: any) => tranformEvent(item),
      );

      setEvents(transformBody);
    } else {
      setEvents([]);
    }
  }, [data]);

  const handleDragStart = useCallback(() => {}, []);

  const handleEventDrop = useCallback(
    ({ event, start, end }: EventInteractionArgs<any>) => {
      setEvents((prev: any) =>
        prev.map((m: any) => {
          if (m.id === event.id) {
            return {
              ...m,
              start,
              end,
            };
          }
          return m;
        }),
      );

      mutate({
        jobId: event.id,
        jobData: {
          startDate: dayjs(start).format("YYYY-MM-DD"),
          dueDate: dayjs(end).format("YYYY-MM-DD"),
        },
      } as JobPayload);
    },
    [setEvents],
  );

  const handleEventResize = useCallback(
    ({ event, start, end }: EventInteractionArgs<any>) => {
      setEvents((prev: any) =>
        prev.map((m: any) => {
          if (m.id === event.id) {
            return {
              ...m,
              start,
              end,
            };
          }
          return m;
        }),
      );

      mutate({
        jobId: event.id,
        jobData: {
          startDate: dayjs(start).format("YYYY-MM-DD"),
          dueDate: dayjs(end).format("YYYY-MM-DD"),
        },
      } as JobPayload);
    },
    [setEvents],
  );

  const eventStyleGetter = (event: any) => {
    let style: React.CSSProperties = {};

    if (calendarView === "agenda") {
      style = {
        backgroundColor:
          event.issues && Object.entries(event.issues).length > 0
            ? "hsl(var(--warning-foreground))"
            : "hsl(var(--primary-foreground))",
        padding: "2px 5px",
        border: "none",
      };
    } else {
      style = {
        backgroundColor:
          event.issues && Object.entries(event.issues).length > 0
            ? "hsl(var(--warning))"
            : "hsl(var(--primary))",
        color: "hsl(var(--primary-foreground))",
        borderRadius: "5px",
        padding: "2px 5px",
      };
    }
    return {
      style,
    };
  };

  const dayStyleGetter = () => {
    const style: React.CSSProperties = {
      color: "hsl(var(--muted-foreground))",
    };
    return { style };
  };

  const CustomToolbar = (props: any) => {
    const { label, onNavigate, views, date } = props;

    const handleNavigation = (action: string) => {
      const newDate = dayjs(date);

      if (action === "PREV") {
        newDate.subtract(1, defineView(calendarView));
      } else if (action === "NEXT") {
        newDate.add(1, defineView(calendarView));
      } else if (action === "TODAY") {
        newDate
          .set("year", newDate.year())
          .set("month", newDate.month())
          .set("date", newDate.date());
      }

      setCurrentDate(newDate.toDate());
      onNavigate(action, newDate.toDate());
    };

    return (
      <div className="aero:flex aero:justify-between aero:items-center aero:p-2 aero:mb-2">
        <div>
          <Button
            onClick={() => handleNavigation("PREV")}
            intent="custom"
            design="outline"
            className="mr-2"
          >
            Back
          </Button>
          <Button
            onClick={() => {
              handleNavigation("NEXT");
            }}
            intent="custom"
            design="outline"
          >
            Next
          </Button>
        </div>
        <h2 className="aero:!text-xl aero:!font-semibold">{label}</h2>
        <div className="aero:flex aero:items-center">
          <Select
            value={calendarView}
            onValueChange={(newView) =>
              setCalendarView(newView as CalendarView)
            }
          >
            <SelectTrigger className="aero:w-[180px]">
              <SelectValue placeholder="Select view" />
            </SelectTrigger>
            <SelectContent>
              {views.map((name: string) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => handleNavigation("TODAY")}
            intent="custom"
            design="outline"
            className="aero:ml-2"
          >
            Today
          </Button>
        </div>
      </div>
    );
  };

  const CustomEvent = ({ event }: any) => {
    const isMonthView = calendarView === "month";
    const maxMachines = isMonthView ? 1 : 2;
    const maxTechnicians = isMonthView ? 1 : 2;
    const maxIssues = isMonthView ? 1 : 2;
    // const additionalIssues = Object.entries(event.issues).slice(maxIssues);

    return (
      <div
        className="rbc-event-content aero:p-1 aero:overflow-hidden"
        title={event.title}
      >
        <div className="aero:flex aero:flex-col aero:h-full">
          <div className="aero:flex aero:justify-between aero:items-center aero:mb-1">
            <div className="aero:!font-semibold aero:!text-sm aero:truncate aero:flex aero:items-center aero:gap-2">
              <span>{event.title}</span>
              {showPriorityIcon(event.priority)}
            </div>
            <div className="aero:!text-xs aero:opacity-75 aero:whitespace-nowrap aero:flex aero:gap-2">
              <div>
                {" "}
                {event.dueDate
                  ? dayjs(event.dueDate).format("DD MMM YYYY")
                  : "-"}{" "}
              </div>
              <span> | </span>
              <div>
                {event.standardHours.slice(0, 2)} <span>hrs</span>
              </div>
            </div>
          </div>

          {event.issues && Object.entries(event.issues).length > 0 && (
            <div className="aero:mb-1">
              <ul className="aero:!text-xs aero:space-y-0.5 aero:list-none aero:border aero:border-gray/20 aero:dark:border-brand-primary-foreground/20 aero:!rounded-md aero:p-1.5 aero:bg-gray/5 aero:dark:bg-brand-primary-foreground/5">
                {Object.entries(event.issues)
                  .slice(0, maxIssues)
                  .map(([key, issue]) => (
                    <li key={key} className="aero:flex aero:items-center">
                      <span className="aero:w-1.5 aero:h-1.5 aero:!rounded-full aero:bg-brand-primary-foreground/60 aero:mr-1.5 aero:flex-shrink-0"></span>
                      <span className="aero:!font-medium aero:mr-1">
                        {capitalize(key)}:
                      </span>
                      <span className="aero:truncate">{issue as string}</span>
                    </li>
                  ))}
                {/* {additionalIssues.length > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <li className='text-xs italic flex items-center cursor-pointer'>
                          <AlertTriangle className='w-3 h-3 mr-1' />
                          More issues...
                        </li>
                      </TooltipTrigger>
                      <TooltipContent className='bg-secondary'>
                        <ul className='text-xs space-y-1'>
                          {additionalIssues.map(([key, issue]) => (
                            <li key={key} className='text-black'>
                              <span className='font-medium '>{capitalize(key)}:</span>{' '}
                              {issue as string}
                            </li>
                          ))}
                        </ul>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )} */}
              </ul>
            </div>
          )}

          {event.technicians && event.technicians.length > 0 && (
            <div className="aero:flex aero:items-center aero:mb-1">
              <User className="aero:w-3 aero:h-3 aero:mr-1" />
              <div className="aero:!text-xs aero:truncate">
                {event.technicians
                  .slice(0, maxTechnicians)
                  .map((tech: Technician) => tech.name)
                  .join(", ")}
                {event.technicians.length > maxTechnicians && "..."}
              </div>
            </div>
          )}
          {event.machines && event.machines.length > 0 && (
            <div className="aero:flex aero:items-center aero:mb-1">
              <Wrench className="aero:w-3 aero:h-3 aero:mr-1" />
              <div className="aero:!text-xs aero:truncate">
                {event.machines
                  .slice(0, maxMachines)
                  .map((machine: Machine) => machine.machineName)
                  .join(", ")}
                {event.machines.length > maxMachines && "..."}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="aero:flex aero:justify-center aero:items-center">
        <DragAndDropCalendar
          localizer={localizer}
          events={events}
          backgroundEvents={events}
          view={calendarView}
          date={currentDate}
          defaultView="month"
          defaultDate={dayjs().toDate()}
          onDragStart={handleDragStart}
          onEventDrop={handleEventDrop}
          onEventResize={handleEventResize}
          eventPropGetter={eventStyleGetter}
          dayPropGetter={dayStyleGetter}
          onSelectEvent={(event) => {
            const eventItem = event as ExtendedCalendarItem;

            setSelectedItem((prev) => ({
              show: !prev.show,
              task:
                data?.data.find((f: any) => f.id === eventItem.id) ||
                defaultSelectedItem,
            }));
          }}
          onNavigate={(value) => {
            setCurrentDate(dayjs(value).toDate());
          }}
          onView={(value) => {
            setCalendarView(value as CalendarView);
          }}
          components={{
            toolbar: CustomToolbar,
            event: CustomEvent,
          }}
          popup
          popupOffset={{ x: 30, y: 20 }}
          resizable
          selectable
          step={60}
          timeslots={10}
          showMultiDayTimes
          style={{ height: "calc(100vh - 60px)", width: "98%" }}
          className="aero:bg-background aero:border aero:!rounded-lg aero:shadow-lg"
          showAllEvents={true}
        />
      </div>

      <KanbanDrawerContent
        open={selectedItem.show}
        onOpenChange={(open) =>
          setSelectedItem((prev) => ({
            ...prev,
            show: open,
          }))
        }
        data={selectedItem.task}
        viewMode
      />
    </>
  );
}
